/**
 * Endpoint methods.
 *
 * A thin, typed-by-convention layer over the Axios instance. Each method
 * returns already-unwrapped data so callers never touch the response envelope.
 * `signal` is threaded through everywhere so TanStack Query can cancel
 * in-flight requests (important for search-as-you-type).
 */

import { unwrap, unwrapData, cleanParams } from './client.js';

/**
 * Builds the endpoint surface bound to one Axios instance.
 * @param {import('axios').AxiosInstance} http
 */
export function createEndpoints(http) {
  return {
    /* ------------------------------------------------------------ system */
    system: {
      health: (signal) => http.get('/health', { signal, skipAuth: true }).then(unwrapData),
      ready: (signal) => http.get('/ready', { signal, skipAuth: true }).then(unwrapData),

      /**
       * The app version policy and this client's standing against it.
       *
       * `X-Client` / `X-Client-Version` are sent automatically, so the usual
       * call is `api.system.appVersion()`; `params` is for support tooling
       * asking about a platform and version other than its own.
       *
       * Unauthenticated, because the gate has to answer before sign-in and
       * without a token. Short timeout, because app launch waits on it and the
       * 30s mobile default exists for song uploads. `skipClientOutdated` so
       * this request can never trip the gate it exists to clear.
       *
       * Returns `{ data, meta }` — `meta.reason` and `meta.policySource` are
       * what a debug screen needs to explain an unexpected verdict.
       */
      appVersion: (params, signal) =>
        http
          .get('/app-version', {
            params: cleanParams(params),
            signal,
            skipAuth: true,
            timeout: 6000,
            skipClientOutdated: true,
          })
          .then(unwrap),
    },

    /* -------------------------------------------------------------- auth */
    auth: {
      /** Creates or refreshes the MongoDB profile for the signed-in Firebase user. */
      sync: (body = {}) => http.post('/auth/sync', body).then(unwrapData),
      me: (signal) => http.get('/auth/me', { signal }).then(unwrapData),
    },

    users: {
      updateProfile: (body) => http.patch('/users/me', body).then(unwrapData),
      updatePreferences: (body) => http.patch('/users/me/preferences', body).then(unwrapData),
      deleteAccount: (body) => http.delete('/users/me', { data: body }).then(unwrapData),
    },

    /* ----------------------------------------------------------- billing */
    billing: {
      status: (signal) => http.get('/billing/status', { signal }).then(unwrapData),
      sync: (body = { reason: 'manual' }) => http.post('/billing/sync', body).then(unwrapData),
      limits: (signal) => http.get('/billing/limits', { signal }).then(unwrapData),
      usage: (signal) => http.get('/billing/usage', { signal }).then(unwrapData),
      management: (signal) => http.get('/billing/management', { signal }).then(unwrapData),
    },

    /* ------------------------------------------------------------- songs */
    songs: {
      list: (params, signal) =>
        http.get('/songs', { params: cleanParams(params), signal }).then(unwrap),
      search: (params, signal) =>
        http.get('/songs/search', { params: cleanParams(params), signal }).then(unwrap),
      get: (songId, signal) => http.get(`/songs/${songId}`, { signal }).then(unwrapData),
      create: (body) => http.post('/songs', body).then(unwrapData),
      update: (songId, body) => http.patch(`/songs/${songId}`, body).then(unwrapData),
      remove: (songId) => http.delete(`/songs/${songId}`).then(unwrapData),
      arrangements: (songId, signal) =>
        http.get(`/songs/${songId}/arrangements`, { signal }).then(unwrap),
    },

    /* ------------------------------------------------------ arrangements */
    arrangements: {
      get: (arrangementId, signal) =>
        http.get(`/arrangements/${arrangementId}`, { signal }).then(unwrapData),
      create: (body) => http.post('/arrangements', body).then(unwrapData),
      /**
       * Updates an arrangement.
       * `body.revision` must carry the revision the client edited — the server
       * answers 409 REVISION_CONFLICT if it has moved on since.
       */
      update: (arrangementId, body) =>
        http.patch(`/arrangements/${arrangementId}`, body).then(unwrapData),
      remove: (arrangementId) => http.delete(`/arrangements/${arrangementId}`).then(unwrapData),
      duplicate: (arrangementId, body = {}) =>
        http.post(`/arrangements/${arrangementId}/duplicate`, body).then(unwrapData),
      requestPublication: (arrangementId, body) =>
        http.post(`/arrangements/${arrangementId}/publish-request`, body).then(unwrapData),
      recordView: (arrangementId, body = {}) =>
        http.post(`/arrangements/${arrangementId}/view`, body).then(unwrapData),
      importChordPro: (body) => http.post('/arrangements/import/chordpro', body).then(unwrapData),
      importPlainText: (body) => http.post('/arrangements/import/text', body).then(unwrapData),
      exportChordPro: (arrangementId, signal) =>
        http.get(`/arrangements/${arrangementId}/export/chordpro`, { signal }).then(unwrapData),
    },

    /* --------------------------------------------------------- favorites */
    favorites: {
      list: (params, signal) =>
        http.get('/favorites', { params: cleanParams(params), signal }).then(unwrap),
      add: (arrangementId) => http.put(`/favorites/${arrangementId}`).then(unwrapData),
      remove: (arrangementId) => http.delete(`/favorites/${arrangementId}`).then(unwrapData),
      check: (arrangementId, signal) =>
        http.get(`/favorites/check/${arrangementId}`, { signal }).then(unwrapData),
    },

    /* --------------------------------------------------------- playlists */
    playlists: {
      list: (params, signal) =>
        http.get('/playlists', { params: cleanParams(params), signal }).then(unwrap),
      get: (playlistId, signal) => http.get(`/playlists/${playlistId}`, { signal }).then(unwrapData),
      create: (body) => http.post('/playlists', body).then(unwrapData),
      update: (playlistId, body) => http.patch(`/playlists/${playlistId}`, body).then(unwrapData),
      remove: (playlistId) => http.delete(`/playlists/${playlistId}`).then(unwrapData),
      duplicate: (playlistId, body = {}) =>
        http.post(`/playlists/${playlistId}/duplicate`, body).then(unwrapData),
      /** Chooses which playlist stays editable when a Premium plan lapses to Free. */
      selectFreePlaylist: (body) =>
        http.post('/playlists/select-free-playlist', body).then(unwrapData),

      addItem: (playlistId, body) =>
        http.post(`/playlists/${playlistId}/items`, body).then(unwrapData),
      updateItem: (playlistId, itemId, body) =>
        http.patch(`/playlists/${playlistId}/items/${itemId}`, body).then(unwrapData),
      removeItem: (playlistId, itemId) =>
        http.delete(`/playlists/${playlistId}/items/${itemId}`).then(unwrapData),
      reorder: (playlistId, body) =>
        http.put(`/playlists/${playlistId}/reorder`, body).then(unwrapData),
    },

    /* ----------------------------------------------------------- history */
    history: {
      recent: (params, signal) =>
        http.get('/history/recent', { params: cleanParams(params), signal }).then(unwrap),
      record: (arrangementId, body = {}) =>
        http.put(`/history/recent/${arrangementId}`, body).then(unwrapData),
      clearOne: (arrangementId) => http.delete(`/history/recent/${arrangementId}`).then(unwrapData),
      clearAll: () => http.delete('/history/recent').then(unwrapData),
    },

    /* ------------------------------------------------------------ chords */
    chords: {
      list: (params, signal) =>
        http.get('/chords', { params: cleanParams(params), signal }).then(unwrap),
      /** Looks up diagrams for a chord symbol, including enharmonic aliases. */
      resolve: (params, signal) =>
        http.get('/chords/resolve', { params: cleanParams(params), signal }).then(unwrapData),
      suggestions: (params, signal) =>
        http.get('/chords/suggestions', { params: cleanParams(params), signal }).then(unwrapData),
      get: (chordId, signal) => http.get(`/chords/${chordId}`, { signal }).then(unwrapData),
    },

    /* ------------------------------------------------------------- learn */
    learn: {
      /** The whole path, with lock state and progress already merged in. */
      curriculum: (signal) => http.get('/learn/curriculum', { signal }).then(unwrapData),
      /** Gamification state for the Home and Profile progress cards. */
      profile: (signal) => http.get('/learn/profile', { signal }).then(unwrapData),
      badges: (signal) => http.get('/learn/badges', { signal }).then(unwrapData),

      lesson: (lessonId, signal) => http.get(`/learn/lessons/${lessonId}`, { signal }).then(unwrapData),
      /**
       * Reports a graded result. The server clamps everything here against the
       * lesson's own reward and a daily ceiling — see learnService.js.
       */
      completeLesson: (lessonId, body) =>
        http.post(`/learn/lessons/${lessonId}/complete`, body).then(unwrapData),

      daily: (signal) => http.get('/learn/daily', { signal }).then(unwrapData),
      completeDaily: (body) => http.post('/learn/daily/complete', body).then(unwrapData),

      /** Ear-training categories with this learner's accuracy merged in. */
      listening: (signal) => http.get('/learn/listening', { signal }).then(unwrapData),
      completeListening: (body) => http.post('/learn/listening/complete', body).then(unwrapData),

      equipCosmetic: (body) => http.post('/learn/cosmetics/equip', body).then(unwrapData),
    },

    /* ----------------------------------------------------------- reports */
    reports: {
      create: (body) => http.post('/reports', body).then(unwrapData),
      mine: (params, signal) =>
        http.get('/reports/mine', { params: cleanParams(params), signal }).then(unwrap),
    },

    /* ----------------------------------------------------------- uploads */
    uploads: {
      /**
       * @param {'avatar'|'playlist-cover'|'song-artwork'} kind
       * @param {FormData} formData must contain a `file` field
       */
      upload: (kind, formData, onUploadProgress) =>
        http
          .post(`/uploads/${kind}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress,
          })
          .then(unwrapData),
    },

    /* ------------------------------------------------------------- admin */
    admin: {
      dashboard: (signal) => http.get('/admin/dashboard', { signal }).then(unwrapData),

      /**
       * What the server is actually enforcing as the app version policy, where
       * it came from, and any warnings raised parsing it. Read-only — the
       * policy is authored in the Firebase Console. `refresh` busts the cache
       * so a Console change can be confirmed without waiting out the TTL.
       */
      appVersionPolicy: (signal) =>
        http.get('/admin/app-version-policy', { signal }).then(unwrapData),
      refreshAppVersionPolicy: () =>
        http.post('/admin/app-version-policy/refresh').then(unwrapData),

      users: (params, signal) =>
        http.get('/admin/users', { params: cleanParams(params), signal }).then(unwrap),
      user: (userId, signal) => http.get(`/admin/users/${userId}`, { signal }).then(unwrapData),
      updateUserStatus: (userId, body) =>
        http.patch(`/admin/users/${userId}/status`, body).then(unwrapData),
      updateUserRole: (userId, body) =>
        http.patch(`/admin/users/${userId}/role`, body).then(unwrapData),
      syncUserSubscription: (userId) =>
        http.post(`/admin/users/${userId}/subscription-sync`).then(unwrapData),

      subscriptions: (params, signal) =>
        http.get('/admin/subscriptions', { params: cleanParams(params), signal }).then(unwrap),
      subscription: (userId, signal) =>
        http.get(`/admin/subscriptions/${userId}`, { signal }).then(unwrapData),

      songs: (params, signal) =>
        http.get('/admin/songs', { params: cleanParams(params), signal }).then(unwrap),
      createSong: (body) => http.post('/admin/songs', body).then(unwrapData),
      updateSong: (songId, body) => http.patch(`/admin/songs/${songId}`, body).then(unwrapData),
      deleteSong: (songId) => http.delete(`/admin/songs/${songId}`).then(unwrapData),

      arrangements: (params, signal) =>
        http.get('/admin/arrangements', { params: cleanParams(params), signal }).then(unwrap),
      arrangement: (arrangementId, signal) =>
        http.get(`/admin/arrangements/${arrangementId}`, { signal }).then(unwrapData),
      approveArrangement: (arrangementId, body) =>
        http.post(`/admin/arrangements/${arrangementId}/approve`, body).then(unwrapData),
      rejectArrangement: (arrangementId, body) =>
        http.post(`/admin/arrangements/${arrangementId}/reject`, body).then(unwrapData),
      removeArrangement: (arrangementId, body) =>
        http.post(`/admin/arrangements/${arrangementId}/remove`, body).then(unwrapData),

      chords: (params, signal) =>
        http.get('/admin/chords', { params: cleanParams(params), signal }).then(unwrap),
      createChord: (body) => http.post('/admin/chords', body).then(unwrapData),
      updateChord: (chordId, body) => http.patch(`/admin/chords/${chordId}`, body).then(unwrapData),
      deleteChord: (chordId) => http.delete(`/admin/chords/${chordId}`).then(unwrapData),

      reports: (params, signal) =>
        http.get('/admin/reports', { params: cleanParams(params), signal }).then(unwrap),
      updateReport: (reportId, body) =>
        http.patch(`/admin/reports/${reportId}`, body).then(unwrapData),

      auditLogs: (params, signal) =>
        http.get('/admin/audit-logs', { params: cleanParams(params), signal }).then(unwrap),

      /* Learn curriculum authoring. */
      learnUnits: (params, signal) =>
        http.get('/admin/learn/units', { params: cleanParams(params), signal }).then(unwrap),
      createLearnUnit: (body) => http.post('/admin/learn/units', body).then(unwrapData),
      updateLearnUnit: (unitId, body) =>
        http.patch(`/admin/learn/units/${unitId}`, body).then(unwrapData),
      deleteLearnUnit: (unitId) => http.delete(`/admin/learn/units/${unitId}`).then(unwrapData),
      reorderLearnUnits: (ids) => http.put('/admin/learn/units/reorder', { ids }).then(unwrapData),

      learnLessons: (params, signal) =>
        http.get('/admin/learn/lessons', { params: cleanParams(params), signal }).then(unwrap),
      learnLesson: (lessonId, signal) =>
        http.get(`/admin/learn/lessons/${lessonId}`, { signal }).then(unwrapData),
      createLearnLesson: (body) => http.post('/admin/learn/lessons', body).then(unwrapData),
      updateLearnLesson: (lessonId, body) =>
        http.patch(`/admin/learn/lessons/${lessonId}`, body).then(unwrapData),
      deleteLearnLesson: (lessonId) =>
        http.delete(`/admin/learn/lessons/${lessonId}`).then(unwrapData),
      reorderLearnLessons: (ids) =>
        http.put('/admin/learn/lessons/reorder', { ids }).then(unwrapData),
    },
  };
}
